/**
 * The cues shown at a time over a picture (1.9.0), for the asset player, the editor's program viewer and Side by side:
 * every active cue, each in its own cell, laid out with contracts cueLayout (the burn-in's numbers) and its text built
 * from cueRuns (italic, bold, underline; never HTML). The look comes from CAPTION as inline styles, so the preview and
 * the burn-in cannot drift apart.
 */
import { useEffect, useRef, useState, type CSSProperties, type HTMLAttributes, type ReactNode } from 'react';
import { CAPTION, cueBoxCss, cueContainerCss, cueLayout, cueRuns, effectiveCueStyle, type Cue, type CueLayout, type TrackStyle } from '@prism/contracts';

export interface PictureRect { left: number; top: number; width: number; height: number }

/** The cues shown at `ticks`, by start (cues in different rows may be shown at once). */
export const activeCues = (cues: readonly Cue[], ticks: number): Cue[] =>
  cues.filter((c) => ticks >= c.startTicks && ticks < c.endTicks).sort((a, b) => a.startTicks - b.startTicks);

/** One cue's container and box styles in a picture of w × h CSS pixels. */
export function cueStyles(w: number, h: number, trackStyle: TrackStyle | null | undefined, cue: Pick<Cue, 'text' | 'style'>, subtitleBottom?: number): { layout: CueLayout; container: CSSProperties; box: CSSProperties } {
  const layout = cueLayout(w, h, effectiveCueStyle(trackStyle, cue), subtitleBottom);
  const fontPx = Math.max(8, layout.fontPx);
  return { layout, container: cueContainerCss(layout) as CSSProperties, box: { ...(cueBoxCss(layout) as CSSProperties), fontSize: `${fontPx}px` } };
}

/** A cue's text as runs (the speaker first, in the speaker colour). */
export function CueText({ cue }: { cue: Pick<Cue, 'text' | 'speaker'> }) {
  return (
    <>
      {cue.speaker && <span className="speaker" style={{ color: CAPTION.speakerColor }}>{cue.speaker}: </span>}
      {cueRuns(cue.text).map((r, i) => (
        <span key={i} style={r.i || r.b || r.u ? { fontStyle: r.i ? 'italic' : undefined, fontWeight: r.b ? 800 : undefined, textDecoration: r.u ? 'underline' : undefined } : undefined}
          data-run={[r.i && 'i', r.b && 'b', r.u && 'u'].filter(Boolean).join('') || undefined}>{r.text}</span>
      ))}
    </>
  );
}

export function CueOverlay({ cues, trackStyle, ticks, rect, subtitleBottom, className = '', testId = 'subtitle-overlay', lineProps, children }: {
  cues: readonly Cue[];
  trackStyle?: TrackStyle | null;
  ticks: number;
  /** Where the picture is in the overlay's positioned parent; null: the whole parent. */
  rect: PictureRect | null;
  subtitleBottom?: number;
  className?: string;
  testId?: string;
  /** Extra props for a cue's box (the editor's subtitle-line drag). */
  lineProps?: (cue: Cue, layout: CueLayout) => HTMLAttributes<HTMLSpanElement> & { title?: string };
  /** Drawn on top (the drag's readout). */
  children?: ReactNode;
}) {
  const own = useRef<HTMLDivElement>(null);
  const [parent, setParent] = useState<{ width: number; height: number } | null>(null);
  const shown = activeCues(cues, ticks);
  // Without a picture rectangle (sizes not known yet, an audio file): the parent's box.
  useEffect(() => {
    const el = own.current?.parentElement;
    if (rect || !el) return;
    const measure = () => setParent((p) => (p && p.width === el.clientWidth && p.height === el.clientHeight ? p : { width: el.clientWidth, height: el.clientHeight }));
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [rect, shown.length > 0]);
  if (!shown.length) return null;
  const box = rect ?? (parent ? { left: 0, top: 0, ...parent } : null);
  return (
    <div ref={own} className={`subtitle-overlay ${className}`} aria-live="polite" data-testid={testId}
      style={box ? { left: box.left, top: box.top, width: box.width, height: box.height } : { inset: 0 }}>
      {box && box.width > 0 && shown.map((c) => {
        const s = cueStyles(box.width, box.height, trackStyle, c, subtitleBottom);
        return (
          <div key={c.id} className="cue-cell" style={s.container} data-align={`${s.layout.row}${s.layout.column}`} data-cue={c.id}>
            <span dir="auto" style={s.box} data-testid="subtitle-line" data-row={s.layout.row} {...lineProps?.(c, s.layout)}><CueText cue={c} /></span>
          </div>
        );
      })}
      {children}
    </div>
  );
}
